import React from 'react';
import { Head } from '@inertiajs/react';
import AppLayout from '../AppLayout';
import AccountDetails from '@/components/account/AccountDetails';
import ChangePassword from '@/components/account/ChangePassword';
import PlanUsage from '@/components/account/PlanUsage';
import AccountNav from '@/components/account/AccountNav';
import RemoveAccount from '@/components/account/RemoveAccount';
import NoteEncryptionPreference from '@/components/account/NoteEncryptionPreference';
import { useAccountData } from '@/api/account';
import { Spinner, Alert } from '@/components/ui';

const Account = () => {
  const { data, loading, error, refetch } = useAccountData();

  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <Spinner size={50} enableTheme />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mb-4 mx-4">
        <Alert
          type="danger"
          title="Failed to load account data"
          showIcon
          closable
          duration={0}
          onClose={() => window.location.reload()}
        >
          <div className="space-y-2">
            <p>{error}</p>
            <button
              onClick={() => refetch()}
              className="text-sm text-error hover:text-error-dark underline"
            >
              Try again
            </button>
          </div>
        </Alert>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <>
      <Head>
        <title>Account Settings</title>
      </Head>

      <div className="w-full px-4 md:px-6 lg:px-8 py-6">
        {/* Page heading */}
        <div className="max-w-[960px] mx-auto mb-6">
          <h1 className="text-2xl font-bold mb-1">Account Settings</h1>
          <p className="text-gray-600 dark:text-gray-400">
            Manage your account information and security settings
          </p>
        </div>

        {/* Mobile nav */}
        <div className="max-w-[960px] mx-auto mb-6 lg:hidden">
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-3">
            <AccountNav />
          </div>
        </div>

        {/* Two-column layout */}
        <div className="max-w-[960px] mx-auto flex gap-8 items-start">

          {/* Sticky sidebar — desktop only */}
          <aside className="hidden lg:block w-48 shrink-0 sticky top-6">
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 px-3 mb-3">
                Settings
              </p>
              <AccountNav />
            </div>
          </aside>

          {/* Content */}
          <div className="flex-1 min-w-0 space-y-6 pb-[60vh]">
            <section id="account-details" className="scroll-mt-6">
              <AccountDetails initialName={data.name} email={data.email} />
            </section>

            <section id="change-password" className="scroll-mt-6">
              <ChangePassword />
            </section>

            <section id="note-encryption" className="scroll-mt-6">
              <NoteEncryptionPreference
                enabled={Boolean(data.notes_encrypt_by_default)}
              />
            </section>

            <section id="plan-usage" className="scroll-mt-6">
              <PlanUsage />
            </section>

            <section id="remove-account" className="scroll-mt-6">
              <RemoveAccount />
            </section>
          </div>
        </div>
      </div>
    </>
  );
};

Account.layout = (page: React.ReactNode) => <AppLayout children={page} />;
export default Account;
